import React, {useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import IMAGES from "@/public/assets/img";
import Head from "next/head";
import { WEB_URL } from "@/config/endpoints";
import dealerFinancing from "@/config/dealer_financing_config";
import AccordianFaq from "@/component/shared/accordianfaq";
import KeyFeatures from "@/component/dealerFinancing/keyFeatures";
import HowItWorks from "@/component/dealerFinancing/howItWorks";
import ProgramResult from "@/component/dealerFinancing/programResult";
import IndusteryUseCases from "@/component/dealerFinancing/industeryUseCases";
import PrimarySales from "@/component/dealerFinancing/primarySales";
import BenefitsOEMs from "@/component/dealerFinancing/benefitsOEMs";
import CapitalEfficiency from "@/component/dealerFinancing/capitalEfficiency";
import { generateFaqSchema } from "@/component/shared/faqSchema";
import TrustedSection from "@/component/dealerFinancing/trustedSection";

const CorporateLoan = () => {
  const router = useRouter();
  const faqSchema = generateFaqSchema(dealerFinancing.Faq);
  const sectionRef = useRef(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      const headerOffset = 110;
      const elementPosition = sectionRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <main className="pt-[110px]">
      <Head>
        <title>{dealerFinancing.seoData.title}</title>
        <meta
          name="description"
          content={dealerFinancing.seoData.description}
        />
        {/* OpenGraph tags */}
        <meta property="og:title" content={dealerFinancing.seoData.title} />
        <meta
          property="og:description"
          content={dealerFinancing.seoData.description}
        />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>
      <section className="bg-[#E9F0F8] sm:pt-6">
        <div className="container xm:px-2 relative flex justify-between items-center sm:flex-col gap-2">
          <div className="w-[54%] sm:w-full">
            <h1
              className="text-brand mb-2 sm:mb-xsp"
              dangerouslySetInnerHTML={{
                __html: dealerFinancing.bannerHeading,
              }}
            />
            <h2 className="text-lg sm:text-sm text-brand font-medium md:text-base2 mb-1">
              {dealerFinancing.subheading}
            </h2>
            <p className="text-sm text-baseBlack">{dealerFinancing.paraLoan}</p>
            <button
              className="mt-5 flex gap-2 bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C] text-white px-5 py-2 rounded-full text-base sm:py-1 font-normal whitespace-nowrap"
              onClick={scrollToSection}
            >
              {dealerFinancing.btntext}
              <svg
                xmlns="https://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="brand"
                width="24"
                height="24"
                className="transition-transform duration-200 transform group-hover:translate-x-[20px] fill-current text-white"
              >
                <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path>
              </svg>{" "}
            </button>
          </div>
          <div className="w-[43%] sm:w-full sm:mt-6">
            <Image
              src={dealerFinancing.heroBanner}
              alt="Corporate Loan"
              width={453}
              height={530}
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>
      <TrustedSection trustData={dealerFinancing.benefits}/>
      <PrimarySales data={dealerFinancing.primarySales} />
      <ProgramResult data={dealerFinancing.setUpProgram} onButtonClick={scrollToSection}/>
      <HowItWorks data={dealerFinancing.HowItWorks} />
      <KeyFeatures data={dealerFinancing.keyFeatures} />
      <BenefitsOEMs data={dealerFinancing.benefitsOEMs} />
      <IndusteryUseCases industry={dealerFinancing.industryUseCases} />
      <CapitalEfficiency data={dealerFinancing.capitalEfficency} />

      <section className="bg-white pb-sp-100 sm:pb-sp-big">
        <div className="container px-3 xm:px-2">
          <div>
            <h3 className="mb-6 font-medium text-center text-lg text-secondary">
              {dealerFinancing.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={dealerFinancing.Faq} />
            </div>
          </div>
        </div>
      </section>
      <div ref={sectionRef}></div>
    </main>
  );
};

export default CorporateLoan;
